import React, { useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";

import NotificationToast from "../Helpers/NotificationToast";
import { Formik, Form, Field } from "formik";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faTimes,
    faEye,
    faPaperclip,
    faEnvelopesBulk,
    faInfoCircle,
    faCommentSmile,
    faCommentXmark,
    faRobot,
    faLocationCheck,
    faPencil,
    faCheck,
    faImageSlash,
    faTrash,
    faDownload,
    faSackDollar,
    faHandHoldingDollar,
    faLocationXmark,
    faUserGroup,
    faUser,
    faFloppyDisk,
    faCircleQuestion,
    faSignature,
} from "@fortawesome/pro-regular-svg-icons";
import { faFile, faFileCsv, faFileExcel, faFilePdf, faGif, faImage, faMicrophone } from "@fortawesome/pro-solid-svg-icons";
import { getBooking } from "../../store/booking/bookingActions";
import { faFileZipper, faLoader, faVideo } from "@fortawesome/pro-regular-svg-icons";
import { closeModal, openModal } from "../../store/modal/modalReducer";
import { toast } from "react-toastify";
import FileTypeMapModal from "../Modals/FileTypeMapModal";
import ModalViewSignatures from "../Modals/ModalViewSignatures";

export default function FileDisplay(props) {
    let file = props.file;
    let callback = props.callback;
    let currentPreview = props.currentPreview;
    let onFileChange = props.onFileChange; // New callback for file changes
    let showJointToggle = props.showJointToggle; // Show joint applicant toggle
    let compactMode = props.compactMode; // Compact mode shows only name and preview button
    const [loading, setLoading] = useState(false);
    const [isJoint, setIsJoint] = useState(file.is_joint || false);
    const [applicantType, setApplicantType] = useState(file.applicant_type || 'main');
    const [isCompany, setIsCompany] = useState(file.is_company || false);
    const [showFileTypeModal, setShowFileTypeModal] = useState(false);
    const [showSignaturesModal, setShowSignaturesModal] = useState(false);
    const dispatch = useDispatch();
    const { current_booking } = useSelector((state) => state.booking);
    const { settings, years } = useSelector((state) => state.settings);
    const { current_staff, staff } = useSelector((state) => state.auth);
    
    let icon = faFile;

    switch (file.extension) {
        case "pdf":
            icon = faFilePdf;
            break;
        case "csv":
            icon = faFileCsv;
            break;
        case "xsl":
        case "xlsx":
            icon = faFileExcel;
            break;
        case "jpg":
        case "png":
        case "jpeg":
            icon = faImage;
            break;
        case "gif":
            icon = faGif;
            break;
        case "zip":
        case "tar":
            icon = faFileZipper;
        case "mp4":
            icon = faVideo;
            break;
        case "mp3":
            icon = faMicrophone;
            break;
        default:
            icon = faFile;
            break;
    }

    async function toggle_file(new_status) {
        setLoading(true);
        await axios({
            method: "post",
            url: `${process.env.REACT_APP_API_URL}toggle_file`,
            data: { id: file.id, status: new_status },
        })
            .then(() => {
                if (onFileChange) {
                    onFileChange();
                } else {
                    dispatch(getBooking(current_booking.id));
                }
            })
            .catch((response) => {
                <NotificationToast title={"Something went wrong"} message={response.message} type="error" />;
            })
            .finally(() => {
                setLoading(false);

                if (callback) {
                    dispatch(closeModal());
                    dispatch(
                        openModal({
                            modal_type: "ModalViewFiles",
                            modal_props: {
                                files: current_booking.files,
                            },
                        })
                    );
                }
            });
    }

    async function download_file(file) {
        setLoading(true);
        await axios({
            method: "get",
            responseType: "blob",
            url: `${process.env.REACT_APP_API_URL}download_file/${file.id}`,
        })
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", file.name + "." + file.extension);
                document.body.appendChild(link);
                link.click();
            })
            .catch(function (error) {
                toast(<NotificationToast title="File Missing" message={"Something went wrong, we could not download this file!"} type="error" />);
            })
            .finally(() => setLoading(false));
    }

    async function delete_file(id) {
        setLoading(true);
        await axios({
            method: "delete",
            url: `${process.env.REACT_APP_API_URL}delete_file_admin/${id}`,
        })
            .then((response) => {
                if (onFileChange) {
                    onFileChange();
                } else {
                    dispatch(getBooking(current_booking.id));
                }
            })
            .finally(() => {
                setLoading(false);
                if (callback) {
                    dispatch(closeModal());
                    dispatch(
                        openModal({
                            modal_type: "ModalViewFiles",
                            modal_props: {
                                files: current_booking.files,
                            },
                        })
                    );
                }
            });
    }

    function handleDeleteClick(id) {
        if (window.confirm("Are you sure you want to delete this file? This action cannot be undone.")) {
            delete_file(id);
        }
    }

    function classNames(...classes) {
        return classes.filter(Boolean).join(" ");
    }

    async function make_payment(file_id) {
        setLoading(true);
        await axios({
            method: "get",
            url: `${process.env.REACT_APP_API_URL}mark_payment/${file_id}`,
        })
            .then((response) => {
                if (onFileChange) {
                    onFileChange();
                } else {
                    dispatch(getBooking(current_booking.id));
                }
            })
            .finally(() => {
                setLoading(false);
            });
    }

    async function make_pop(file_id) {
        setLoading(true);
        await axios({
            method: "get",
            url: `${process.env.REACT_APP_API_URL}mark_pop/${file_id}`,
        })
            .then((response) => {
                if (onFileChange) {
                    onFileChange();
                } else {
                    dispatch(getBooking(current_booking.id));
                }
            })
            .finally(() => {
                setLoading(false);
            });
    }

    async function map_file(file_id, year_id) {
        setLoading(true);
        await axios({
            method: "get",
            url: `${process.env.REACT_APP_API_URL}mark_tag/${file_id}/${year_id}`,
        })
            .then((response) => {
                if (onFileChange) {
                    onFileChange();
                } else {
                    dispatch(getBooking(current_booking.id));
                }
            })
            .finally(() => {
                setLoading(false);
            });
    }

    async function toggle_joint(file_id) {
        setLoading(true);
        await axios({
            method: "post",
            url: `${process.env.REACT_APP_API_URL}toggle_joint/${file_id}`,
        })
            .then((response) => {
                setIsJoint(response.data.is_joint);
                if (onFileChange) {
                    onFileChange();
                } else {
                    dispatch(getBooking(current_booking.id));
                }
            })
            .catch((error) => {
                toast(<NotificationToast title="Error" message="Failed to toggle joint status" type="error" />);
            })
            .finally(() => {
                setLoading(false);
            });
    }

    async function toggle_applicant_type(file_id) {
        setLoading(true);
        // Toggle between: main <-> co_applicant
        const nextType = applicantType === 'main' ? 'co_applicant' : 'main';
        
        await axios({
            method: "post",
            url: `${process.env.REACT_APP_API_URL}set_applicant_type/${file_id}`,
            data: { applicant_type: nextType }
        })
            .then((response) => {
                setApplicantType(response.data.applicant_type);
                setIsJoint(response.data.is_joint);
                setIsCompany(response.data.is_company);
                if (onFileChange) {
                    onFileChange();
                } else {
                    dispatch(getBooking(current_booking.id));
                }
            })
            .catch((error) => {
                toast(<NotificationToast title="Error" message="Failed to toggle applicant type" type="error" />);
            })
            .finally(() => {
                setLoading(false);
            });
    }

    async function toggle_company_status(file_id) {
        setLoading(true);
        
        await axios({
            method: "post",
            url: `${process.env.REACT_APP_API_URL}toggle_company/${file_id}`,
        })
            .then((response) => {
                setIsCompany(response.data.is_company);
                setApplicantType(response.data.applicant_type);
                setIsJoint(response.data.is_joint);
                if (onFileChange) {
                    onFileChange();
                } else {
                    dispatch(getBooking(current_booking.id));
                }
            })
            .catch((error) => {
                toast(<NotificationToast title="Error" message="Failed to toggle company status" type="error" />);
            })
            .finally(() => {
                setLoading(false);
            });
    }

    // Compact mode - show name, eye (preview), tick (approve), cross (decline) only
    if (compactMode) {
        return (
            <li className={`px-2 py-1.5 flex items-center text-sm ${
                currentPreview === file.id ? 'bg-primary/10 border-l-2 border-primary' : 'hover:bg-gray-50'
            }`}>
                {loading ? (
                    <FontAwesomeIcon icon={faLoader} className="mx-auto text-gray-600 text-lg animate-spin" />
                ) : (
                    <>
                        <button
                            type="button"
                            onClick={() => {
                                props.setShowFile(true);
                                props.setFileId(file.id);
                            }}
                            className={`p-1 rounded flex-shrink-0 ${
                                currentPreview === file.id 
                                    ? 'text-primary' 
                                    : 'text-gray-400 hover:text-gray-600'
                            }`}
                            title="Preview File"
                        >
                            <FontAwesomeIcon icon={faEye} className="h-3 w-3" />
                        </button>
                        <div className="flex items-center min-w-0 flex-1 ml-1">
                            <span className={`truncate text-xs ${
                                currentPreview === file.id ? 'text-primary font-medium' : 'text-gray-600'
                            }`} title={file.name}>
                                {file.name}
                            </span>
                        </div>
                        {/* Single signature icon - opens modal with all signatures */}
                        {(file.signature_id || file.signature_joint_id || file.signature_landlord_id) && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowSignaturesModal(true);
                                }}
                                className="p-1 rounded ml-1 flex-shrink-0 text-blue-500 hover:text-blue-700"
                                title="View Signatures"
                            >
                                <FontAwesomeIcon icon={faSignature} className="h-3 w-3" />
                            </button>
                        )}
                        {file.created_at && (
                            <span className="text-[10px] text-gray-400 flex-shrink-0 ml-2" title={new Date(file.created_at).toLocaleString()}>
                                {new Date(file.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} {new Date(file.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                        )}
                        {showJointToggle && (
                            <>
                                <button
                                    type="button"
                                    onClick={() => toggle_applicant_type(file.id)}
                                    className={`p-1 rounded ml-1 flex-shrink-0 ${
                                        applicantType === 'co_applicant' ? 'text-blue-600' : 'text-green-600'
                                    }`}
                                    title={applicantType === 'co_applicant' ? "Co-Applicant (click for Main)" : "Main Applicant (click for Co-Applicant)"}
                                >
                                    <FontAwesomeIcon icon={faUser} className="h-3 w-3" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => toggle_company_status(file.id)}
                                    className={`p-1 rounded ml-1 flex-shrink-0 ${
                                        isCompany ? 'text-purple-600' : 'text-gray-400'
                                    }`}
                                    title={isCompany ? "Company File (click to remove)" : "Not Company (click to mark as Company)"}
                                >
                                    <FontAwesomeIcon icon={faUserGroup} className="h-3 w-3" />
                                </button>
                            </>
                        )}
                    </>
                )}
            </li>
        );
    }

    return (
        <li className="pl-3 pr-4 py-3 flex items-center justify-between text-sm">
            {loading ? (
                <FontAwesomeIcon icon={faLoader} className="mx-auto text-gray-600 text-lg animate-spin" />
            ) : (
                <Formik
                    initialValues={{
                        name: file.name,
                    }}
                    onSubmit={async (values, { setSubmitting }) => {
                        setSubmitting(true);
                        setLoading(true);

                        await axios({
                            method: "put",
                            url: `${process.env.REACT_APP_API_URL}rename_file/${file.id}`,
                            data: {
                                name: values.name,
                            },
                        })
                            .then((response) => {
                                if (onFileChange) {
                                    onFileChange();
                                } else {
                                    dispatch(getBooking(current_booking.id));
                                }
                                toast(<NotificationToast title="File Name Updated Succesfully" message={"Name changed to " + response.data} />);
                            })
                            .catch((error) => {
                                toast(<NotificationToast title="Something went wrong" message={error.response} type="error" />);
                            })
                            .finally(() => {
                                setTimeout(() => {
                                    setLoading(false);
                                }, 1000);
                            });
                    }}
                >
                    {({ handleSubmit, values }) => (
                        <Form onSubmit={handleSubmit} className="w-full flex">
                            {props && !props.onScreen && (
                                <FontAwesomeIcon
                                    icon={faEye}
                                    onClick={() => {
                                        props.setShowFile(true);
                                        props.setFileId(file.id);
                                    }}
                                    className={
                                        currentPreview === file.id
                                            ? "text-primary hover:text-primary cursor-pointer mr-5 hidden md:block"
                                            : "text-gray-400 hover:text-gray-800 cursor-pointer mr-5 hidden md:block"
                                    }
                                    title="Preview File"
                                />
                            )}
                            <div className="w-0 flex-1 flex items-center">
                                <FontAwesomeIcon className="flex-shrink-0 h-5 w-5 text-gray-600" icon={icon} aria-hidden="true" />
                                <Field
                                    className={currentPreview === file.id ? "ml-2 flex-1 w-0 truncate bg-transparent text-primary" : "ml-2 flex-1 w-0 truncate text-gray-600 bg-transparent"}
                                    name="name"
                                    placeholder={file.name}
                                    title={file.name}
                                />
                                {file && file.rename === 1 && current_staff?.hideAppInfoUpdate != 1 && values.name !== file.name && (
                                    <FontAwesomeIcon icon={faFloppyDisk} onClick={() => handleSubmit()} className="ml-1 text-primary hover:text-primary_hover cursor-pointer flex-shrink-0" title="Save Name" />
                                )}
                                {/* Single signature icon - opens modal with all signatures */}
                                {(file.signature_id || file.signature_joint_id || file.signature_landlord_id) && (
                                    <FontAwesomeIcon
                                        icon={faSignature}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowSignaturesModal(true);
                                        }}
                                        className="ml-2 text-blue-500 hover:text-blue-700 cursor-pointer flex-shrink-0"
                                        title="View Signatures"
                                    />
                                )}
                            </div>
                            {file.created_at && (
                                <span className="text-xs text-gray-400 flex-shrink-0 ml-4" title={new Date(file.created_at).toLocaleString()}>
                                    {new Date(file.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} {new Date(file.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                            )}
                            <div className="ml-4 flex-shrink-0 flex items-center space-x-1">
                                {props.mapped === false ? (
                                    <div className="flex">
                                        <div className="font-medium text-gray-400 hover:text-gray-800 cursor-pointer" onClick={() => map_file(file.id, props.moveYear.id)}>
                                            <FontAwesomeIcon icon={faLocationCheck} title="Map File" /> &nbsp;
                                            <span>To {props.moveYear.year}</span>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {current_staff?.hideAppInfoUpdate != 1 && (
                                            <>
                                                <FontAwesomeIcon
                                                    icon={faCheck}
                                                    onClick={() => setShowFileTypeModal(true)}
                                                    className={classNames(file.status === "Approved" ? "text-green-600 font-bold" : "", "text-gray-400 hover:text-gray-800 cursor-pointer")}
                                                    title="Approve File"
                                                />
                                                <FontAwesomeIcon
                                                    icon={faTimes}
                                                    onClick={() => toggle_file("Declined")}
                                                    className={classNames(file.status === "Declined" ? "text-red-600" : "", "text-gray-400 hover:text-gray-800 cursor-pointer")}
                                                    title="Decline File"
                                                />
                                                <FontAwesomeIcon
                                                    icon={faCircleQuestion}
                                                    onClick={() => toggle_file("Intervention")}
                                                    className={classNames(file.status === "Intervention" ? "text-red-600 font-bold" : "", "text-gray-400 hover:text-gray-800 cursor-pointer")}
                                                    title="Needs Intervention (Manual Review Required)"
                                                />
                                                <FontAwesomeIcon
                                                    icon={faImageSlash}
                                                    onClick={() => toggle_file("Ignored")}
                                                    className={classNames(file.status === "Ignored" ? "text-yellow-600" : "", "text-gray-400 hover:text-gray-800 cursor-pointer")}
                                                    title="Ignore File (best used for email signatures)"
                                                />
                                                <FontAwesomeIcon icon={faTrash} onClick={() => handleDeleteClick(file.id)} className="text-gray-400 hover:text-gray-800 cursor-pointer" title="Delete File" />
                                            </>
                                        )}
                                        {current_staff?.hideAppInfoUpdate != 1 && (
                                            <>
                                                <FontAwesomeIcon
                                                    icon={faDownload}
                                                    onClick={() => download_file(file)}
                                                    className="font-medium text-gray-400 hover:text-gray-800 cursor-pointer"
                                                    title="Download File"
                                                />
                                                <FontAwesomeIcon
                                                    icon={faSackDollar}
                                                    onClick={() => make_payment(file.id)}
                                                    className={classNames(
                                                        file.legacy_file_type === "Payment" || 
                                                        (typeof file.file_type === 'string' && file.file_type === "Payment") || 
                                                        (typeof file.file_type === 'object' && file.file_type?.name === "Payment") 
                                                        ? "text-green-600 font-bold" : "", 
                                                        "text-gray-400 hover:text-gray-800 cursor-pointer"
                                                    )}
                                                    title="Tag Payment"
                                                />
                                                <FontAwesomeIcon
                                                    icon={faHandHoldingDollar}
                                                    onClick={() => make_pop(file.id)}
                                                    className={classNames(
                                                        file.legacy_file_type === "POP" || 
                                                        (typeof file.file_type === 'string' && file.file_type === "POP") || 
                                                        (typeof file.file_type === 'object' && file.file_type?.name === "POP") 
                                                        ? "text-green-600 font-bold" : "", 
                                                        "text-gray-400 hover:text-gray-800 cursor-pointer"
                                                    )}
                                                    title="Tag POP"
                                                />
                                                {showJointToggle && (
                                                    <>
                                                        <FontAwesomeIcon
                                                            icon={faUser}
                                                            onClick={() => toggle_applicant_type(file.id)}
                                                            className={classNames(
                                                                applicantType === 'co_applicant' ? "text-blue-600" : "text-green-600",
                                                                "cursor-pointer"
                                                            )}
                                                            title={applicantType === 'co_applicant' ? "Co-Applicant (click for Main)" : "Main Applicant (click for Co-Applicant)"}
                                                        />
                                                        <FontAwesomeIcon
                                                            icon={faUserGroup}
                                                            onClick={() => toggle_company_status(file.id)}
                                                            className={classNames(
                                                                isCompany ? "text-purple-600" : "text-gray-400",
                                                                "cursor-pointer"
                                                            )}
                                                            title={isCompany ? "Company File (click to remove)" : "Not Company (click to mark as Company)"}
                                                        />
                                                    </>
                                                )}
                                                {file.year_id != settings.year.id && (
                                                    <>
                                                        <div className="font-medium text-gray-400 hover:text-gray-800 cursor-pointer" onClick={() => map_file(file.id, settings.year.id)}>
                                                            <FontAwesomeIcon icon={faLocationCheck} title="Map File" /> &nbsp;
                                                            <span>To {settings.year.year}</span>
                                                        </div>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        </Form>
                    )}
                </Formik>
            )}
            
            {showFileTypeModal && (
                <FileTypeMapModal
                    file={file}
                    onClose={() => setShowFileTypeModal(false)}
                    onSuccess={() => {
                        setShowFileTypeModal(false);
                        if (onFileChange) {
                            onFileChange();
                        } else {
                            dispatch(getBooking(current_booking.id));
                        }
                    }}
                />
            )}
            
            {/* Signatures Modal */}
            {showSignaturesModal && (
                <ModalViewSignatures
                    file={file}
                    onClose={() => setShowSignaturesModal(false)}
                />
            )}
        </li>
    );
}
